<template>
  <div class="ttsContainer" :class="mode">
    <InputArea @handleSubmit="handleSubmit" />
    <div class="outputArea">
      <header class="outputHeader">
        <div class="contentArea">
          <el-tooltip v-if="backfillText.length > 0" :disabled="backfillText.length <= 5" placement="top">
            <span slot="content">
              <p style="display: block; max-width: 500px">{{ backfillText }}</p>
            </span>
            <span class="inputText">{{ backfillText }}</span>
          </el-tooltip>
          <span v-show="generatedVoice.informant_tag" class="styleText">音色：{{ generatedVoice.informant_tag || '' }}</span>
        </div>
        <div class="buttonArea">
          <el-button
            class="headerButton"
            type="text"
            size="mini"
            :disabled="isDisabledButton"
            @click="handleDownload">
            <i class="el-icon-download"/>下载音频
          </el-button>
          <el-button
            type="text"
            size="mini"
            :disabled="isDisabledButton"
            @click="handleReSubmit"
            class="headerButton">
            <i class="el-icon-refresh"/>重新生成
          </el-button>
          <el-button v-if="outputMode.includes('insert') && generatedVoice.file_path"
            class="headerButton"
            type="text"
            size="mini"
            @click="insertMethod">
            <i class="iconfont">&#xe7f2;</i>插入
          </el-button>
        </div>
      </header>
      <div v-if="isShowText" class="outputShowArea">
        <div class="showTextArea">
          <h3>处理成功后结果将会显示在这里</h3>
          <br>
          <span>可点击【下载音频】使用或在【素材库】中找到处理完成的素材进行使用。</span>
        </div>
      </div>
      <div v-else-if="isShowLoading" class="outputShowArea">
        <img style="width: 100px; height: 100px;" :src="loadingImg" alt="Animated GTF"/>
        <span class="showPendingText">结果正在生成中...</span>
      </div>
      <div
        v-else-if="isShowAudio && generatedVoice.file_path"
        :class="['outputShowArea', 'generateVoiceArea']">
        <audio v-if="generatedVoice.file_path" controls :src="generatedVoice.file_path"></audio>
      </div>
      <div
        v-else-if="isShowErrorMessage"
        class="errorMessage">
        <img :src="failImg"/>
        音频生成有误
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import InputArea from './components/InputArea.vue'
import loadingImg from '/imgs/AILoading.png';
import failImg from '/imgs/fail.png'
import { GeneratedVoiceStatus } from './config'
import { GeneratedServiceStatus, LoadingStatus } from '../config'

import { useGetIdentifyInfo } from '@/hooks/useIdentifyInfo';
import { getIdentifyInfo } from "@/api/common"

export default {
  name: 'TextToSpeech',
  props: {
    mode: {
      type: String,
    },
    aiType: {
      type: String,
    },
    inputMode: {
      type: Array,
      default: () => [],
    },
    outputMode: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    InputArea,
  },
  provide() {
    return {
      mode: this.mode
    }
  },
  data() {
    return {
      source: axios.CancelToken.source(),

      inputParams: {},

      backfillText: '',
      usageLogId: '',
      generatedVoice: {},
      selectedOption: {},
      btnLoading: false,
      loading: false,
      pollingTimer: null,
      identifyInfo: {},

      loadingImg,
      failImg
    };
  },

  async mounted() {
    this.identifyInfo = await useGetIdentifyInfo(this.aiType, getIdentifyInfo, this);
    this.$emit('emitAccountInfo', this.identifyInfo)
  },

  computed: {
    isShowText() {
      // 展示引导文字条件：没有任务ID，没有图片路径，没有图片描述
      return !this.usageLogId && !this.generatedVoice.file_path && !this.generatedVoice.text
    },
    isShowLoading() {
      return !!this.usageLogId
    },
    isShowErrorMessage() {
      return (!this.generatedVoice.file_path && [GeneratedVoiceStatus.FAILED, GeneratedVoiceStatus.CANCELED, GeneratedVoiceStatus.SUSPENDED, GeneratedVoiceStatus.UNKNOWN, LoadingStatus.CREATE, LoadingStatus.ENQUEUE, LoadingStatus.DEQUEUE, LoadingStatus.RUNNING].includes(this.generatedVoice.status))
    },
    isShowAudio() {
      // 展示图片条件：图片生成成功时；图片生成有误时，同样没有file_path，此时也要展示Image
      return !!this.generatedVoice.file_path || (!this.generatedVoice.file_path && ![GeneratedServiceStatus.FAILED, GeneratedServiceStatus.CANCELED, GeneratedServiceStatus.SUSPENDED, GeneratedServiceStatus.UNKNOWN, LoadingStatus.ERROR].includes(this.generatedImg.status))
    },
    isDisabledButton() {
      return this.btnLoading || !this.generatedVoice.file_path;
    }
  },

  watch: {
    usageLogId: function(newVal) {
      if (newVal) {
        // 关闭前一次轮询，生成新的source,开始轮训
        this.stopPolling();
        this.generateNewSource();
        this.startPolling();
      } else {
        this.stopPolling();
      }
    }
  },

  methods: {
    generateNewSource() {
      const source = axios.CancelToken.source();

      this.source = source;
      // 暂时缓存目前选择的文本和类型
      this.backfillText = this.inputParams?.text || ''
      this.generatedVoice.style = this.selectedOption.tag
    },

    async handleDownload() {
      try {
        const response = await fetch(this.generatedVoice.file_path);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // 将响应转换为 Blob 对象
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        // 创建一个隐藏的 <a> 元素
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'file.mp3';

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // 释放临时 URL 对象
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('下载失败:', error);
      }
    },

    async handleReSubmit() {
      this.btnLoading  = true;
      this.generatedVoice.file_path = undefined;

      const { text, informant_id, volume, speech_rate, folder_id } = this.generatedVoice;

      const params = {
        text,
        informant_id,
        volume,
        folder_id,
        speech_rate: Number(speech_rate)
      };

      const { data } = await axios.post('/index/tts/submit', params);

      if (data.status === 0) {
        this.usageLogId = data?.data?.usage_log_id;
      } else {
        this.$message.error(data.error);
      }

      this.$emit('updateHistory');

      this.btnLoading  = false;
    },

    async handleSubmit(inputData, selectedOption) {
      if(!inputData.text || !inputData.text.trim() ) {
        this.$message.error('请输入内容');

        return;
      }

      if(!inputData.informant_id) {
        this.$message.error('请选择声音');

        return;
      }

      this.selectedOption = selectedOption;

      this.inputParams = inputData;

      this.generatedVoice = {};
      this.btnLoading = true;
      this.backfillText = '';

      try {
        const { data } = await axios.post('/index/tts/submit', {
          ...inputData,
          speech_rate: Number(inputData.speech_rate)
        });

        if (data.status === 0) {
          this.usageLogId = data?.data?.usage_log_id;
        } else {
          this.$message.error(data.error);
        }

        this.$emit('updateHistory');
      } catch (err) {
        console.log(err)
      } finally {
        this.btnLoading  = false;
      }
    },

    async getGeneratedVoice () {
      this.loading = true;

      try {
        const { data } = await axios.get('/index/tts/get_usage_log', {
          params: {
            usage_log_id: this.usageLogId
          },
          cancelToken: this.source.token
        });

        if (data.status === 0) {
          this.generatedVoice = data?.data;
          this.backfillText = data?.data?.text || '';

          if (![LoadingStatus.CREATE, LoadingStatus.ENQUEUE, LoadingStatus.DEQUEUE, LoadingStatus.RUNNING].includes(data.data.status)) {
            this.usageLogId = undefined;
            this.$emit('updateHistory');
          }
        } else {
          this.$message.error(data.error);
          this.stopPolling();
          this.loading = false;
        }
      } catch(err) {
        console.error(err)

      } finally {
        this.loading = false;
      }
    },

    useHistory(item) {
      const { text, status, id } = item;

      this.backfillText = text;
      this.source.cancel('操作取消');

      if ([LoadingStatus.CREATE, LoadingStatus.ENQUEUE, LoadingStatus.DEQUEUE, LoadingStatus.RUNNING].includes(status)) {
        this.usageLogId = id;
      } else {
        this.usageLogId = undefined;
        this.loading = false;
      }

      this.generatedVoice = item;
    },

    insertMethod() {
      const _data = { ...this.generatedVoice, url: this.generatedVoice.file_path}
      this.$emit('insertMethod', _data)
    },

    startPolling() {
      this.pollingTimer = setInterval(() => {
        this.getGeneratedVoice()
      }, 3000);
    },

    stopPolling() {
      clearInterval(this.pollingTimer);
    },
  },

  beforeDestroy() {
    this.source.cancel('操作取消');
    this.stopPolling()
  }
};
</script>

<style lang="scss" scoped>
.iconfont {
  margin-right: 2px;
}
.ttsContainer {
  width: 100%;
  height: 100%;
  display: flex;

  .outputArea {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 350px;
    position: relative;
    border-left: 1px solid #DDDDDD;

    .outputHeader {
      padding: 0 20px;
      height: 40px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #F5F5F5;

      .contentArea {
        min-width: 210px;
        max-width: 330px;
        display: flex;

        .inputText {
          display: inline-block;
          width: 180px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          line-height: 30px;
        }
        .styleText {
          margin-left: 10px;
          display: inline-block;
          min-width: 140px;
          line-height: 30px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

    .outputShowArea {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      .showPendingText {
        font-size: 18px;
        background-image: linear-gradient(to right, #E7B0E6 100%, #9371FF 100%, #49A3FF 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .showTextArea {
        min-width: 300px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        h3 {
          color: #666;
          font-size: 20px;
        }
        span {
          color: #999;
          font-size: 14px;
        }
      }
    }

    .generateVoiceArea {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .errorMessage {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-size: 14px;
    }

    .buttonArea {
      color: #666666;
      display: flex;
      justify-content: flex-end;

      .el-button {
        color: #666666;
        border: none
      }

      ::v-deep .el-button--text:focus {
        background-color: transparent;
      }

      .headerButton{
        margin-left: 10px;
        font-size: 14px;
        text-align: center;
        color: #666;
        i {
          font-size: 18px;
          vertical-align: middle;
        }
        &:not(.is-disabled):hover {
          color: #FD7700;
        }
        .el-icon-download .el-icon-refresh {
          font-size: 20px;
        }
        &.is-disabled {
          color: #999;
        }
      }

      .el-button:hover {
        background-color: transparent;
      }
      .el-button:active {
        background-color: transparent;
      }
    }
  }
}

.h5 {
  border: 1px solid #444444;

  .outputArea {
    border-left: 1px solid #444444;
    .outputHeader {
      background: #444;
      color: #CCC;
    }
    .outputShowArea {
      .showTextArea {
        h3 {
          color: #CCCCCC;
          margin-block-start: 0px;
          margin-block-end: 0px;
        }
        span {
          color: #999999;
        }
      }
    }
    .buttonArea {
      color: #CCC !important;
      .el-button {
        color: #CCC !important;
      }
    }
  }

  ::v-deep .el-button:hover .el-button:active .el-button:focus {
    background: transparent;
  }
}

.rich {
  border: 1px solid #DDDDDD;
}
</style>
